import React, { useEffect, useState } from "react";
import {
  Dropdown,
  Modal,
  Pagination,
  Tabs,
  Card,
  notification as antNotif,
} from "antd";
import {
  BellFilled,
  CheckCircleFilled,
  FileTextFilled,
  StarFilled,
  EditFilled,
  AuditOutlined,
  DeleteOutlined,
  MailOutlined,
  EllipsisOutlined,
  ReadOutlined,
  CheckOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { openNotification } from "../../helper/openNotification";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/lang";
import { Severty, ShowToast } from "../../helper/toast";
import Main from "../../components/layout/Main";
import PushNotification from "./Notification";

const { TabPane } = Tabs;

const getNotifMeta = (text = "") => {
  const d = text.toLowerCase();
  // Must come before the "publish" check below — a client-approval message
  // ("... approved \"X\". It's ready to publish.") contains the substring
  // "publish" too, so without this it was wrongly labeled "Published"
  // instead of "Approved".
  if (d.includes("approv"))   return { icon: CheckCircleFilled, hue: "green",  label: "Approved"  };
  if (d.includes("publish"))  return { icon: CheckCircleFilled, hue: "green",  label: "Published" };
  if (d.includes("review"))   return { icon: AuditOutlined,    hue: "blue",   label: "Review"    };
  if (d.includes("creat"))    return { icon: EditFilled,       hue: "purple", label: "Created"   };
  if (d.includes("blog"))     return { icon: ReadOutlined,     hue: "amber",  label: "Blog"      };
  if (d.includes("vendor"))   return { icon: TeamOutlined,     hue: "teal",   label: "Vendor"    };
  if (d.includes("user") || d.includes("client"))
                              return { icon: UserOutlined,     hue: "indigo", label: "Client"    };
  if (d.includes("strateg") || d.includes("activat") || d.includes("update"))
                              return { icon: StarFilled,       hue: "teal",   label: "Update"    };
  if (d.includes("post"))     return { icon: FileTextFilled,   hue: "indigo", label: "Post"      };
  return                             { icon: BellFilled,       hue: "default",label: "Alert"     };
};

const CATEGORIES = [
  { key: "all",     label: "All",       hue: "default" },
  { key: "unread",  label: "Unread",    hue: "blue"    },
  { key: "green",   label: "Published", hue: "green"   },
  { key: "blue",    label: "Review",    hue: "blue"    },
  { key: "purple",  label: "Created",   hue: "purple"  },
  { key: "amber",   label: "Blog",      hue: "amber"   },
  { key: "teal",    label: "Update",    hue: "teal"    },
  { key: "indigo",  label: "Post",      hue: "indigo"  },
];

const SIDE_CATS = [
  { hue: "green",  label: "Published" },
  { hue: "blue",   label: "Review"    },
  { hue: "purple", label: "Created"   },
  { hue: "amber",  label: "Blog"      },
  { hue: "teal",   label: "Update"    },
  { hue: "indigo", label: "Post"      },
];

export const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination]       = useState({ current: 1, pageSize: 10, total: 0 });
  const [activeFilter, setActiveFilter]   = useState("all");
  const [refresh, setRefresh]             = useState(false);
  const { request }                       = useRequest();
  const navigate                          = useNavigate();

  const showMsg = (msg, type = "success") => antNotif[type]({ message: msg });

  const fetchData = () => {
    request({
      url: `${apiPath.notificationUser}?page=${pagination.current}&limit=${pagination.pageSize}`,
      method: "GET",
      onSuccess: (data) => {
        const grouped = data?.data?.notificationsByDate || [];
        const flat    = grouped.flatMap((g) => g.notifications || []);
        setNotifications(flat);
        setPagination((p) => ({
          ...p,
          total: data?.data?.totalCount || flat.length,
        }));
      },
      onError: (err) => console.error("Notification fetch error:", err),
    });
  };

  const markAllRead = () => {
    request({
      url:    `${apiPath.notification}/read`,
      method: "POST",
      onSuccess: (data) => { showMsg(data?.message || lang("All marked as read")); setRefresh((x) => !x); },
      onError:   (err)  => console.log(err),
    });
  };

  const markOneRead = (id) => {
    request({
      url:    `${apiPath.notification}/read/${id}`,
      method: "POST",
      onSuccess: () => setRefresh((x) => !x),
      onError:   () => {},
    });
  };

  const deleteNotification = (id) => {
    request({
      url:    `${apiPath.notification}/delete/${id}`,
      method: "DELETE",
      onSuccess: (data) => { showMsg(data?.message || lang("Deleted")); setRefresh((x) => !x); },
      onError:   (err)  => { ShowToast(err?.response?.data?.message, Severty.ERROR); },
    });
  };

  const handleDeleteConfirm = (e, id) => {
    e.stopPropagation();
    Modal.confirm({
      title:      lang("Delete Notification"),
      content:    lang("Are you sure you want to delete this notification?"),
      okText:     lang("OK"),
      cancelText: lang("Cancel"),
      centered:   true,
      onOk: () => deleteNotification(id),
    });
  };

  const handleNotificationClick = (item) => {
    const id = item?._id || item?.id;
    if (!item.is_read && !item.isRead) markOneRead(id);

    const useFor = (item?.use_for || "").toLowerCase();
    if (item?.data?.type || item?.data?.route) {
      openNotification({ item, request, navigate });
      return;
    }
    if (useFor === "vendor") { navigate("/vendor-manager"); return; }
    if (useFor === "user")   { navigate("/customer-manager"); return; }
  };

  useEffect(() => { fetchData(); }, [pagination.current, refresh]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const getText = (item) =>
    item?.description || item?.message || item?.title || "";

  const unreadCount = notifications.filter((n) => !(n.isRead || n.is_read)).length;
  const readCount   = notifications.length - unreadCount;
  const readPct     = notifications.length > 0
    ? Math.round((readCount / notifications.length) * 100) : 0;

  const catCounts = {};
  notifications.forEach((n) => {
    const { hue } = getNotifMeta(getText(n));
    catCounts[hue] = (catCounts[hue] || 0) + 1;
  });

  const filteredList = notifications.filter((n) => {
    if (activeFilter === "all")    return true;
    if (activeFilter === "unread") return !(n.isRead || n.is_read);
    return getNotifMeta(getText(n)).hue === activeFilter;
  });

  const menuItems = [
    { key: "read-all",   label: <span><MailOutlined style={{ marginRight: 8 }} />{lang("Mark all as read")}</span>,   onClick: markAllRead },
    { type: "divider" },
  ];

  return (
    <div className="notif-page">
      {/* ── Header ── */}
      <div className="notif-header">
        <div className="notif-header__left">
          <div className="notif-header__icon-wrap">
            <BellFilled className="notif-header__bell" />
          </div>
          <div>
            <h1 className="notif-header__title">{lang("Notifications")}</h1>
            <p className="notif-header__sub">
              {unreadCount > 0
                ? <><span className="notif-count-chip">{unreadCount}</span> {lang("unread messages")}</>
                : lang("You're all caught up!")}
            </p>
          </div>
        </div>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
          <button type="button" className="notif-menu-btn" aria-label="More options">
            <EllipsisOutlined />
          </button>
        </Dropdown>
      </div>

      {/* ── Filter pills ── */}
      {notifications.length > 0 && (
        <div className="notif-filters">
          {CATEGORIES.filter((c) => {
            if (c.key === "all" || c.key === "unread") return true;
            return (catCounts[c.key] || 0) > 0;
          }).map((c) => (
            <button
              key={c.key}
              type="button"
              className={`notif-filter-pill notif-filter-pill--${c.hue}${activeFilter === c.key ? " notif-filter-pill--active" : ""}`}
              onClick={() => setActiveFilter(c.key)}
            >
              {c.label}
              {c.key !== "all" && c.key !== "unread" && catCounts[c.key]
                ? <span className="notif-filter-count">{catCounts[c.key]}</span>
                : null}
              {c.key === "unread" && unreadCount > 0
                ? <span className="notif-filter-count">{unreadCount}</span>
                : null}
            </button>
          ))}
        </div>
      )}

      {/* ── Two-column body ── */}
      <div className="notif-body">

        {/* Feed */}
        <div className="notif-feed">
          {filteredList.length > 0 ? (
            <div className="notif-timeline">
              {filteredList.map((item) => {
                const id      = item?._id || item?.id;
                const text    = getText(item);
                const isRead  = item?.isRead || item?.is_read;
                const ts      = item?.created_at || item?.createdAt;
                const { icon: Icon, hue, label } = getNotifMeta(text);

                return (
                  <div
                    key={id}
                    className={`notif-card notif-card--${hue}${isRead ? " notif-card--read" : " notif-card--unread"}`}
                    onClick={() => handleNotificationClick(item)}
                  >
                    <div className={`notif-card__icon notif-icon--${hue}`}>
                      <Icon />
                    </div>
                    <div className="notif-card__body">
                      <span className={`notif-type-badge notif-badge--${hue}`}>{label}</span>
                      {item?.title && item?.title !== text && (
                        <span className="notif-card__sub-title">{item.title}</span>
                      )}
                      <p className="notif-card__desc">{text}</p>
                      <span className="notif-card__time">
                        {moment(ts).fromNow()} · {moment(ts).format("MMM D, h:mm A")}
                      </span>
                    </div>
                    <div className="notif-card__actions">
                      {!isRead && <span className="notif-pulse-dot" />}
                      <button
                        type="button"
                        className="notif-delete-btn"
                        onClick={(e) => handleDeleteConfirm(e, id)}
                        aria-label="Delete"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="notif-empty">
              <div className="notif-empty__graphic">
                <BellFilled className="notif-empty__bell" />
              </div>
              <h3 className="notif-empty__title">
                {activeFilter === "all" ? lang("All clear!") : `No ${activeFilter} notifications`}
              </h3>
              <p className="notif-empty__sub">
                {activeFilter === "all"
                  ? lang("No notifications yet. We'll let you know when something happens.")
                  : lang("Try switching to a different filter above.")}
              </p>
            </div>
          )}

          {pagination.total > pagination.pageSize && (
            <div className="notif-pagination">
              <Pagination
                current={pagination.current || 1}
                total={pagination.total || 0}
                pageSize={pagination.pageSize || 10}
                showSizeChanger={false}
                onChange={(page) => setPagination((p) => ({ ...p, current: page }))}
              />
            </div>
          )}
        </div>

        {/* ── Right sidebar ── */}
        {notifications.length > 0 && (
          <aside className="notif-sidebar">

            <div className="notif-sidebar__card">
              <p className="notif-sidebar__card-label">{lang("Read rate")}</p>
              <div className="notif-ring-wrap">
                <div className="notif-ring" style={{ "--pct": `${readPct}%` }} />
                <div className="notif-ring-inner">
                  <span className="notif-ring-pct">{readPct}%</span>
                  <span className="notif-ring-sub">{lang("read")}</span>
                </div>
              </div>
              <div className="notif-ring-legend">
                <span className="notif-legend-dot notif-legend-dot--read" />
                <span>{readCount} {lang("read")}</span>
                <span className="notif-legend-dot notif-legend-dot--unread" />
                <span>{unreadCount} {lang("unread")}</span>
              </div>
            </div>

            <div className="notif-sidebar__card">
              <p className="notif-sidebar__card-label">{lang("By category")}</p>
              <div className="notif-cat-list">
                {SIDE_CATS.filter((c) => catCounts[c.hue] > 0).map((c) => (
                  <div
                    key={c.hue}
                    className={`notif-cat-row notif-cat-row--${c.hue}${activeFilter === c.hue ? " notif-cat-row--active" : ""}`}
                    onClick={() => setActiveFilter(activeFilter === c.hue ? "all" : c.hue)}
                  >
                    <span className={`notif-cat-dot notif-cat-dot--${c.hue}`} />
                    <span className="notif-cat-name">{c.label}</span>
                    <span className="notif-cat-count">{catCounts[c.hue]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="notif-sidebar__card notif-sidebar__actions">
              <p className="notif-sidebar__card-label">{lang("Quick actions")}</p>
              <button type="button" className="notif-qa-btn" onClick={markAllRead}>
                <CheckOutlined /> {lang("Mark all as read")}
              </button>
            </div>

          </aside>
        )}
      </div>
    </div>
  );
};


function Index() {
  return (
    <Main>
      <div style={{ padding: "0 4px" }}>
        <Tabs defaultActiveKey="notifications" className="client-tabs">
          <TabPane tab={<span className="tab-label">🔔 {lang("Notifications")}</span>} key="notifications">
            <NotificationList />
          </TabPane>
          <TabPane tab={<span className="tab-label">📢 {lang("Push Notifications")}</span>} key="push">
            <Card bordered={false} style={{ marginTop: 16 }}>
              <PushNotification />
            </Card>
          </TabPane>
        </Tabs>
      </div>
    </Main>
  );
}

export default Index;
